import { useApi } from '../hooks/useApi';
import StatCard from '../components/StatCard';
import SalaryGauge from '../components/SalaryGauge';
import type { DashboardStats, ActivityLog, SalaryBalance, ShopProfile } from '../types';
import './DashboardPage.css';

// ─── 워커 타입 표시 ───────────────────────────────────────────────────────────

const WORKER_LABELS: Record<ActivityLog['workerType'], string> = {
  order: '주문',
  product: '상품',
  customer: '고객',
  promotion: '프로모션',
  analytics: '분석',
  sync: '동기화',
};

const WORKER_ICONS: Record<ActivityLog['workerType'], string> = {
  order: '📦',
  product: '🏷️',
  customer: '👤',
  promotion: '🎁',
  analytics: '📊',
  sync: '🔄',
};

const STATUS_CLASS: Record<ActivityLog['status'], string> = {
  success: 'badge-success',
  failed: 'badge-danger',
  pending: 'badge-warning',
};

const STATUS_LABEL: Record<ActivityLog['status'], string> = {
  success: '완료',
  failed: '실패',
  pending: '처리 중',
};

function formatTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// ─── 더미 데이터 (API 연결 전 미리보기용) ────────────────────────────────────

const MOCK_BALANCE: SalaryBalance = {
  current: 18400,
  total: 59000,
  used: 40600,
  percentage: 68.8,
  resetDate: '2026-04-01',
};

const MOCK_STATS: DashboardStats = {
  todayOrders: 47,
  todayRevenue: 2840000,
  pendingOrders: 12,
  pendingCs: 3,
  orderGrowth: 12.5,
  revenueGrowth: -3.2,
};

const MOCK_LOGS: ActivityLog[] = [
  {
    id: '1',
    workerType: 'order',
    action: '주문 처리',
    description: '신규 주문 12건 배송 준비 상태로 변경',
    status: 'success',
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    shopId: 'shop1',
  },
  {
    id: '2',
    workerType: 'customer',
    action: 'CS 응대',
    description: '배송 문의 3건 자동 답변 완료',
    status: 'success',
    timestamp: new Date(Date.now() - 23 * 60 * 1000).toISOString(),
    shopId: 'shop1',
  },
  {
    id: '3',
    workerType: 'product',
    action: '재고 부족 알림',
    description: '"봄 신상 원피스" 재고 3개 남음 — 발주 필요',
    status: 'pending',
    timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    shopId: 'shop1',
  },
  {
    id: '4',
    workerType: 'sync',
    action: '멀티 플랫폼 동기화',
    description: '카페24 ↔ 아임웹 재고 동기화 실패 (네트워크 오류)',
    status: 'failed',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    shopId: 'shop1',
  },
  {
    id: '5',
    workerType: 'analytics',
    action: '일일 리포트',
    description: '어제 매출 분석 리포트 생성 완료',
    status: 'success',
    timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    shopId: 'shop1',
  },
];

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { data: shops } = useApi<ShopProfile[]>('/shops');
  const { data: stats, isLoading: statsLoading } = useApi<DashboardStats>('/dashboard/stats');
  const { data: balance, isLoading: balanceLoading } = useApi<SalaryBalance>('/billing/balance');
  const { data: logs, isLoading: logsLoading } = useApi<ActivityLog[]>('/dashboard/activities');

  // API 연결 전까지 목 데이터 사용
  const displayStats = stats ?? MOCK_STATS;
  const displayBalance = balance ?? MOCK_BALANCE;
  const displayLogs = logs ?? MOCK_LOGS;

  const primaryShop = shops?.[0];

  return (
    <div className="dashboard">
      {/* 페이지 헤더 */}
      <div className="dashboard__header">
        <div>
          <h2 className="dashboard__title">대시보드</h2>
          {primaryShop && (
            <p className="dashboard__shop-name">
              {primaryShop.mallName}
              <span className="badge badge-success dashboard__shop-badge">연결됨</span>
            </p>
          )}
        </div>
        <div className="dashboard__date">
          {new Date().toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short',
          })}
        </div>
      </div>

      {/* 급여 게이지 */}
      <SalaryGauge balance={displayBalance} isLoading={balanceLoading} />

      {/* 통계 카드 */}
      <section>
        <h3 className="dashboard__section-title">오늘의 요약</h3>
        <div className="dashboard__stats-grid">
          <StatCard
            title="오늘 주문"
            value={displayStats.todayOrders}
            unit="건"
            change={displayStats.orderGrowth}
            icon="📦"
            isLoading={statsLoading}
          />
          <StatCard
            title="오늘 매출"
            value={displayStats.todayRevenue}
            unit="원"
            change={displayStats.revenueGrowth}
            icon="💰"
            isLoading={statsLoading}
          />
          <StatCard
            title="처리 대기"
            value={displayStats.pendingOrders}
            unit="건"
            icon="⏳"
            isLoading={statsLoading}
          />
          <StatCard
            title="미응대 CS"
            value={displayStats.pendingCs}
            unit="건"
            icon="💬"
            isLoading={statsLoading}
          />
        </div>
      </section>

      {/* 최근 작업 이력 */}
      <section>
        <div className="dashboard__section-header">
          <h3 className="dashboard__section-title">최근 AI 팀원 활동</h3>
          <span className="dashboard__section-desc">오늘의 자동 처리 내역</span>
        </div>

        {logsLoading ? (
          <div className="activity-skeleton">
            {[1, 2, 3].map((i) => (
              <div key={i} className="activity-skeleton__item" />
            ))}
          </div>
        ) : (
          <div className="activity-list">
            {displayLogs.map((log) => (
              <div key={log.id} className="activity-item">
                <div className="activity-item__icon">
                  {WORKER_ICONS[log.workerType]}
                </div>
                <div className="activity-item__body">
                  <div className="activity-item__header">
                    <span className="activity-item__worker">
                      {WORKER_LABELS[log.workerType]} 팀원
                    </span>
                    <span className="activity-item__action">{log.action}</span>
                    <span className={`badge ${STATUS_CLASS[log.status]}`}>
                      {STATUS_LABEL[log.status]}
                    </span>
                  </div>
                  <p className="activity-item__desc">{log.description}</p>
                </div>
                <time className="activity-item__time">
                  {formatTime(log.timestamp)}
                </time>
              </div>
            ))}

            {displayLogs.length === 0 && (
              <div className="dashboard__empty">
                <span>📋</span>
                <p>아직 작업 이력이 없습니다.</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
